import * as React from 'react';
import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import { EPageTypes, EPlatformTypes, SimpleCall } from '@cian/frontend-newbuilding-call-component';
import { IconCommunicationCall16, IconCommunicationPhone16 } from '@cian/ui-kit-design-tokens/icons';
import { useSelector } from 'react-redux';

import * as styles from './BuilderCallReasonsContainer.css';
import { OpenedPhone } from './parts/OpenedPhone';
import { trackBuilderCallReasonsShowing } from './tracking';
import { trackShowPhone } from '../../actions/analytics';
import { BuilderCallReasons } from '../../components/BuilderCallReasons';
import { BuilderCallReasonsRedesigned } from '../../components/BuilderCallReasonsRedesigned';
import { LayoutBlock } from '../../components/LayoutBlock';
import { getGaLabel } from '../../selectors/analytics';
import { getBuilders, getNewbuildingId, getSellers } from '../../selectors/newbuilding';
import { useApplicationContext } from '../../utils/applicationContext';
import { setUserOpenedPhoneInNewobject } from '../../utils/businessLogic/lastHopePopup';
import { CallMeBackContainer } from '../CallMeBack';
import { COMPARISON_ENTRYPOINTS_ENABLED_CONFIG } from '../Comparison/constants';
import { FlatViewOrderButtonContainer } from '../FlatViewOrderButton';

const BUTTON_STYLES = {
  size: 'M' as const,
  beforeIcon: <IconCommunicationPhone16 color="icon-inverted-default" />,
};

export const BuilderCallReasonsContainer: React.FC = () => {
  const { config, httpApi, logger } = useApplicationContext();

  const builders = useSelector(getBuilders);
  const sellers = useSelector(getSellers);
  const newbuildingId = useSelector(getNewbuildingId);
  const gaLabel = useSelector(getGaLabel);

  const isComparisonEntrypointsEnabled = config.get<boolean>(COMPARISON_ENTRYPOINTS_ENABLED_CONFIG) || false;

  const handleShowing = React.useCallback(() => {
    trackBuilderCallReasonsShowing();
  }, []);

  const handleShowPhone = React.useCallback(() => {
    setUserOpenedPhoneInNewobject(newbuildingId);
    trackShowPhone(gaLabel, 'Open_ask_builder');
  }, [gaLabel, newbuildingId]);

  const isBuildersWithPhones = builders.filter(builder => builder.phone?.number && builder.showPhone).length > 0;

  const buttonText = isBuildersWithPhones ? 'Контакты застройщика' : 'Контакты продавца';

  const contactsButton = (
    <SimpleCall
      builders={builders}
      sellers={sellers}
      openContactsStyles={BUTTON_STYLES}
      showPhoneBuilderStyles={BUTTON_STYLES}
      showPhoneSellerStyles={BUTTON_STYLES}
      httpApi={httpApi}
      logger={logger}
      onShowPhoneClick={handleShowPhone}
      pageType={EPageTypes.NewbuildingCard}
      platformType={EPlatformTypes.WebDesktop}
      placeType="blockBuilderCallReasons"
      SinglePhoneComponent={OpenedPhone}
      singlePhoneButtonText="Показать телефон"
      multiplePhoneButtonText={buttonText}
    />
  );

  if (!isComparisonEntrypointsEnabled) {
    return (
      <LayoutBlock data-testid="BuilderCallReasonsBlock">
        <ActionAfterViewObserver callback={handleShowing} triggerOnce callbackDelay={1000}>
          <BuilderCallReasons isBuilder={isBuildersWithPhones} contactsButton={contactsButton} />
        </ActionAfterViewObserver>
      </LayoutBlock>
    );
  }

  return (
    <ActionAfterViewObserver
      callback={handleShowing}
      triggerOnce
      callbackDelay={1000}
      wrapperClass={styles['observer-wrapper']}
    >
      <BuilderCallReasonsRedesigned
        isBuilder={isBuildersWithPhones}
        contactsButton={contactsButton}
        callbackButton={
          <CallMeBackContainer
            placeType="floating_block"
            buttonTheme="stroke_primary"
            buttonText=""
            buttonBeforeIcon={<IconCommunicationCall16 color="icon-main-default" />}
          />
        }
        flatViewOrderButton={<FlatViewOrderButtonContainer />}
      />
    </ActionAfterViewObserver>
  );
};
